import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Autocomplete,
	Alert
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserTableData } from "./UsersManagement";
import { ESRState } from "@/types/data";
import { getCurrentUser } from "@/auth";
import {
	createTicket,
	ESRThunkDispatch,
	listAllUsers,
	uploadTicketAttachments,
} from "@/actions/form";
import { TicketFilePicker, validateTicketFiles } from "./Notifications/TicketAttachments";
interface ErrorReportModalProps {
	open: boolean;
	onClose: () => void;
	pageType?: string;
    creatorid?: string;
}

const TICKET_TYPES = [
	"LOGIN",
	"ESR_FORM",
	"QID_MODIFY_RESPONSE",
	"REQUEST_PERMISSIONS",
	"PROJECT_MISSING",
	"OTHER",
];

export const ErrorReportModal: FC<ErrorReportModalProps> = ({
	open,
	onClose,
	pageType,
    creatorid,
}) => {
	const [ticketType, setTicketType] = useState("OTHER");
	const [description, setDescription] = useState("");
	const [title, setTitle] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [creatorId, setCreatorId] = useState("");
	const [esrId, setEsrId] = useState("");
	const [questionId, setQuestionId] = useState("");
	const [ticketRaisedFor, setTicketRaisedFor] = useState<UserTableData | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [submitError, setSubmitError] = useState("");
	const hostname = window.location.hostname;
	const isSignUpPage = hostname.includes("/signup");

	const dispatch = useDispatch<ESRThunkDispatch>();
	const isLoginPage = pageType === "LOGIN";
	// File uploads are only for users logged into TREC; the upload endpoint requires a Keycloak token
	const canAttachFiles =
		!isLoginPage && !isSignUpPage && Boolean(getCurrentUser()?.access_token);
	const fileError = canAttachFiles ? validateTicketFiles(files) : null;
	const user_list: UserTableData[] = useSelector((state: ESRState) => {
			return Object.keys(state.user_list).map((email: string) => {
				const user = state.user_list[email];
				return {
					created: user.created,
					email: email,
					name: user.first_name + " " + user.last_name,
					enabled: user.enabled,
					groups: user.groups,
					access_level: user.admin ? "Admin" : "User",
					actions: true,
				};
			});
		});
	useEffect(() => {
		if (!open) return;

		setDescription("");
		setCreatorId("");
		setEsrId("");
		setQuestionId("");
		setTitle("");
		setTicketRaisedFor(null);
		setFiles([]);
		setSubmitError("");
		if (pageType === "LOGIN") {
			setTicketType("LOGIN");
		} else {
			setTicketType("OTHER");
		}
        if(creatorid?.trim()) setCreatorId(creatorid.trim())
	}, [open, pageType]);
	// fetch users when query changes
	useEffect(() => {
		if(!isSignUpPage && !isLoginPage) dispatch(listAllUsers());
	}, [open, pageType]);

	const handleSubmit = async () => {
		const payload = {
			creator_id: creatorId,
			ticket_type: isLoginPage ? "LOGIN" : ticketType,
			description: description,
			esr_id: esrId,
			title: title,
			question_id: questionId,
			behalf_of_id: ticketRaisedFor?.email || "",
		};
		setSubmitting(true);
		setSubmitError("");
		let ticketId: number;
		try {
			({ ticket_id: ticketId } = await dispatch(createTicket(ticketType, payload)));
		} catch (error) {
			console.error("Failed to submit ticket", error);
			setSubmitError("Failed to submit the ticket. Please try again.");
			return;
		}
		if (canAttachFiles && files.length > 0) {
			try {
				await dispatch(uploadTicketAttachments(ticketId, files));
			} catch (error) {
				// The ticket exists; only the files failed. Keep the dialog open so the user knows.
				console.error("Failed to upload ticket files", error);
				setSubmitError(
					"The ticket was created, but the files could not be uploaded. Please add them in a comment on the ticket.",
				);
				setSubmitting(false);
				return;
			}
		}
		setSubmitting(false);
		setDescription("");
		setTicketType("OTHER");
		setFiles([]);
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Report Error</DialogTitle>
			<DialogContent>
				{/* {!isLoginPage && (
					<TextField
						select
						fullWidth
						margin="normal"
						label="Issue Type"
						value={ticketType}
						onChange={(e) => setTicketType(e.target.value)}
					>
						{TICKET_TYPES.map((type) => (
							<MenuItem key={type} value={type}>
								{type}
							</MenuItem>
						))}
					</TextField>
				)} */}
				<TextField
						select
						fullWidth
						margin="normal"
						label="Issue Type"
						value={ticketType}
						onChange={(e) => setTicketType(e.target.value)}
					>
						{TICKET_TYPES.map((type) => (
							<MenuItem key={type} value={type}>
								{type}
							</MenuItem>
						))}
				</TextField>
				<TextField
					fullWidth
					margin="normal"
					label="Email"
					value={creatorId}
					onChange={(e) => setCreatorId(e.target.value)}
				/>
				<TextField
					fullWidth
					margin="normal"
					label="Title"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					inputProps={{ maxLength: 320 }}
				/>
				{(!isSignUpPage && !isLoginPage) && (<Autocomplete
					options={user_list}
					value={ticketRaisedFor}
					onChange={(_, value) => setTicketRaisedFor(value)}
					getOptionLabel={(option) => `${option.name} (${option.email})`}
					isOptionEqualToValue={(option, value) => option.email === value.email}
					filterOptions={(options, { inputValue }) =>
						options.filter(
							(option) =>
								option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
								option.email.toLowerCase().includes(inputValue.toLowerCase()),
						)
					}
					renderInput={(params) => (
						<TextField
							{...params}
							margin="normal"
							label="Ticket Raised For (Optional)"
							placeholder="Search by name or email"
						/>
					)}
				/>)}

				{(ticketType === "ESR_FORM" ||
					ticketType === "QID_MODIFY_RESPONSE") && (
					<>
						<TextField
							fullWidth
							margin="normal"
							label="ESR ID"
							value={esrId}
							onChange={(e) => setEsrId(e.target.value)}
						/>

						<TextField
							fullWidth
							margin="normal"
							label="Question ID"
							value={questionId}
							onChange={(e) => setQuestionId(e.target.value)}
						/>
					</>
				)}
				{ticketType === "PROJECT_MISSING" && (
					<>
						<TextField
							fullWidth
							margin="normal"
							label="Project Number (and addendum id if it is an addendum)"
							value={esrId}
							onChange={(e) => setEsrId(e.target.value)}
						/>
					</>
				)}
				<TextField
					fullWidth
					multiline
					rows={5}
					margin="normal"
					label="Description"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>
				{canAttachFiles && (
					<TicketFilePicker
						files={files}
						onChange={setFiles}
						multiple
						disabled={submitting}
					/>
				)}
				{fileError && (
					<Alert severity="error" sx={{ mt: 1 }}>
						{fileError}
					</Alert>
				)}
				{submitError && (
					<Alert severity="error" sx={{ mt: 1 }}>
						{submitError}
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={
						submitting ||
						!description.trim() ||
						!creatorId.trim() ||
						Boolean(fileError)
					}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};
